import { useEffect, useRef, useState } from "react";
import api, { apiError, cachedGet } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, UploadCloud, Trash2, FileText, Sparkles, ChevronDown, Package, CalendarClock, CreditCard, Scale, AlertTriangle, Wallet } from "lucide-react";
import { HelpGuide } from "@/components/HelpGuide";
import CardPicker from "@/components/CardPicker";
import CardCompare from "@/components/CardCompare";
import FormulaChooser from "@/components/FormulaChooser";
import PriceModal from "@/components/PriceModal";

const CAT_BADGE = {
  "Logement": "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  "Véhicule": "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  "Santé": "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300",
  "Mobilité & Voyage": "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300",
  "High-Tech": "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300",
  "Responsabilité Civile": "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
};

export default function ContractsView({ onChanged }) {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const [open, setOpen] = useState(null);
  const [showCards, setShowCards] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [formula, setFormula] = useState({ open: false, draft: null, formulas: [], submitting: false });
  const [priceQueue, setPriceQueue] = useState([]);
  const [priceTotal, setPriceTotal] = useState(0);
  const [importFor, setImportFor] = useState(null);
  const [importingId, setImportingId] = useState(null);
  const inputRef = useRef();
  const importRef = useRef();

  const existingCoverageLabels = contracts.flatMap((c) => (c.coverages || []).map((g) => g.label)).filter(Boolean);

  const costLabel = (c) => {
    const mc = c.monthly_cost;
    if (mc == null) return "";
    if (mc === 0) return c.source === "card" ? " · Gratuite" : " · Gratuit";
    const m = Math.round(mc * 100) / 100;
    const y = Math.round(mc * 12);
    return ` · ${m} €/mois · ${y} €/an`;
  };

  const isCardContract = (c) => c.source === "card" || (c.name || "").trim().toLowerCase().startsWith("carte ");

  const importNotice = async (file) => {
    const id = importFor;
    setImportFor(null);
    if (importRef.current) importRef.current.value = "";
    if (!file || !id) return;
    if (file.type !== "application/pdf") {
      toast.error("Merci de déposer un fichier PDF");
      return;
    }
    setImportingId(id);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/contracts/${id}/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setContracts((cs) => cs.map((c) => (c.id === id ? data : c)));
      toast.success(`Garanties importées : ${data.name}`);
      onChanged?.();
      promptPricesFor([data]);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Échec de l'import de la notice");
    } finally {
      setImportingId(null);
    }
  };

  const promptPricesFor = (list) => {
    const missing = (list || []).filter((c) => c && c.monthly_cost == null && !c.needs_import);
    if (missing.length) {
      setPriceQueue(missing);
      setPriceTotal(missing.length);
    }
  };

  const onPriceDone = (updated) => {
    if (updated) setContracts((cs) => cs.map((c) => (c.id === updated.id ? updated : c)));
    setPriceQueue((q) => q.slice(1));
    onChanged?.();
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await cachedGet("/contracts");
      setContracts(data);
    } catch {
      /* hors-ligne sans cache */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Merci de déposer un fichier PDF");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/contracts/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.needs_choice) {
        setFormula({ open: true, draft: data.draft, formulas: data.formulas, submitting: false });
      } else {
        toast.success(`Contrat analysé : ${data.name}`);
        await load();
        onChanged?.();
        promptPricesFor([data]);
      }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Échec de l'analyse");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/contracts/${id}`);
    toast.success("Contrat supprimé");
    await load();
    onChanged?.();
  };

  const setRenewal = async (id, date) => {
    const { data } = await api.patch(`/contracts/${id}`, { renewal_date: date || null });
    setContracts((cs) => cs.map((c) => (c.id === id ? data : c)));
    toast.success("Échéance mise à jour");
    onChanged?.();
  };

  const setCost = async (id, value) => {
    const v = value === "" ? null : parseFloat(String(value).replace(",", "."));
    if (value !== "" && (isNaN(v) || v < 0)) return;
    const { data } = await api.patch(`/contracts/${id}`, { monthly_cost: v });
    setContracts((cs) => cs.map((c) => (c.id === id ? data : c)));
    toast.success("Montant mis à jour");
    onChanged?.();
  };

  const seedDemo = async () => {
    const { data } = await api.post("/contracts/seed-demo");
    setContracts(data);
    toast.success("Contrats de démonstration chargés");
    onChanged?.();
  };

  const confirmFormula = async (chosen) => {
    setFormula((s) => ({ ...s, submitting: true }));
    try {
      const { data } = await api.post("/contracts/upload/confirm", { ...formula.draft, formula: chosen });
      toast.success(`Formule enregistrée : ${data.name}`);
      setFormula({ open: false, draft: null, formulas: [], submitting: false });
      await load();
      onChanged?.();
      promptPricesFor([data]);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Échec de l'enregistrement");
      setFormula((s) => ({ ...s, submitting: false }));
    }
  };

  return (
    <div className="space-y-8 animate-in-up" data-testid="contracts-view">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">Mes contrats</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Déposez & analysez vos polices</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <HelpGuide variant="button" />
          <button data-testid="add-cards-btn" onClick={() => setShowCards(true)} className="flex items-center gap-2 text-sm font-medium px-4 h-10 rounded-xl border border-border hover:bg-muted transition">
            <CreditCard className="w-4 h-4" /> Mes cartes bancaires
          </button>
          <button data-testid="compare-cards-btn" onClick={() => setShowCompare(true)} className="flex items-center gap-2 text-sm font-medium px-4 h-10 rounded-xl border border-border hover:bg-muted transition">
            <Scale className="w-4 h-4" /> Comparer
          </button>
          <button data-testid="seed-demo-btn" onClick={seedDemo} className="flex items-center gap-2 text-sm font-medium px-4 h-10 rounded-xl border border-border hover:bg-muted transition">
            <Package className="w-4 h-4" /> Charger des contrats démo
          </button>
        </div>
      </div>

      {/* Dropzone */}
      <div
        data-testid="dropzone"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); }}
        onClick={() => !uploading && inputRef.current?.click()}
        className={`rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition ${
          drag ? "border-primary bg-accent/50" : "border-border hover:border-primary/50 bg-card"
        }`}
      >
        <input ref={inputRef} type="file" accept="application/pdf" className="hidden" data-testid="file-input" onChange={(e) => upload(e.target.files?.[0])} />
        <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary grid place-items-center mx-auto">
          {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <UploadCloud className="w-6 h-6" />}
        </div>
        <p className="font-heading font-bold mt-4">{uploading ? "Analyse IA en cours…" : "Glissez un PDF ici"}</p>
        <p className="text-sm text-muted-foreground mt-1">
          {uploading ? "Extraction des garanties, franchises et exclusions" : "ou cliquez pour parcourir vos fichiers"}
        </p>
      </div>

      <input ref={importRef} type="file" accept="application/pdf" className="hidden" data-testid="import-file-input" onChange={(e) => importNotice(e.target.files?.[0])} />

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <div className="space-y-3">
          {contracts.map((c) => (
            <div key={c.id} data-testid={`contract-${c.id}`} className="rounded-2xl border border-border bg-card overflow-hidden">
              <div className="p-5 flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-muted grid place-items-center shrink-0">
                  <FileText className="w-5 h-5 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-heading font-bold">{c.name}</p>
                    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${CAT_BADGE[c.category] || "bg-muted text-muted-foreground"}`}>{c.category}</span>
                    {c.source === "pdf" && <span className="text-[10px] font-mono uppercase tracking-wide text-ochre flex items-center gap-1"><Sparkles className="w-3 h-3" />IA</span>}
                    {c.source === "card" && <span className="text-[10px] font-mono uppercase tracking-wide text-primary flex items-center gap-1"><CreditCard className="w-3 h-3" />Carte</span>}
                    {c.needs_import && <span data-testid={`needs-import-badge-${c.id}`} className="text-[10px] font-mono uppercase tracking-wide text-amber-600 dark:text-amber-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />À compléter</span>}
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5">{c.insurer}{costLabel(c)}</p>
                  <p className="text-sm mt-2 text-muted-foreground line-clamp-2">{c.summary}</p>
                  {isCardContract(c) && c.monthly_cost == null && !c.needs_import && (
                    <button
                      data-testid={`add-cost-${c.id}`}
                      onClick={() => { setPriceQueue([c]); setPriceTotal(1); }}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium px-2.5 h-7 rounded-lg border border-primary/40 text-primary hover:bg-primary/10 transition"
                    >
                      <Wallet className="w-3.5 h-3.5" /> Ajouter la cotisation
                    </button>
                  )}
                  {c.needs_import && (
                    <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/60 px-3 py-2">
                      <span className="text-xs text-amber-800 dark:text-amber-300 flex-1 min-w-[180px]">Détails manquants pour cette carte — importez sa notice d'assurance pour une analyse précise.</span>
                      <button
                        data-testid={`import-doc-${c.id}`}
                        onClick={() => { setImportFor(c.id); importRef.current?.click(); }}
                        disabled={importingId === c.id}
                        className="text-xs font-medium px-2.5 h-7 rounded-lg bg-amber-600 text-white hover:opacity-90 disabled:opacity-60 transition inline-flex items-center gap-1 shrink-0"
                      >
                        {importingId === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <UploadCloud className="w-3 h-3" />} {importingId === c.id ? "Analyse…" : "Importer le document"}
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <button data-testid={`delete-${c.id}`} onClick={() => remove(c.id)} className="w-8 h-8 grid place-items-center rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition">
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button data-testid={`contract-expand-${c.id}`} onClick={() => setOpen(open === c.id ? null : c.id)} className="text-xs text-muted-foreground flex items-center gap-1 hover:text-foreground transition">
                    {c.coverages?.length || 0} garanties <ChevronDown className={`w-3.5 h-3.5 transition ${open === c.id ? "rotate-180" : ""}`} />
                  </button>
                </div>
              </div>
              {open === c.id && (
                <div className="border-t border-border bg-muted/30 p-5 grid sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-mono uppercase tracking-wide text-muted-foreground mb-2">Garanties</p>
                    <div className="space-y-2">
                      {(c.coverages || []).map((g, i) => (
                        <div key={i} className="text-sm">
                          <p className="font-medium">{g.label}</p>
                          <p className="text-xs text-muted-foreground">
                            Plafond : {g.limit || "—"} · Franchise : {g.deductible || "—"}{g.notes ? ` · ${g.notes}` : ""}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-mono uppercase tracking-wide text-muted-foreground mb-2">Exclusions</p>
                    <ul className="space-y-1 text-sm text-muted-foreground list-disc list-inside">
                      {(c.exclusions || []).map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </div>
                  <div className="sm:col-span-2 border-t border-border pt-4 flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Wallet className="w-4 h-4" />
                      <span>Montant :</span>
                    </div>
                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        data-testid={`cost-${c.id}`}
                        defaultValue={c.monthly_cost ?? ""}
                        onBlur={(e) => setCost(c.id, e.target.value)}
                        placeholder="—"
                        className="h-9 w-32 pl-3 pr-14 rounded-lg border border-input bg-card text-sm outline-none focus:ring-2 focus:ring-ring/40"
                      />
                      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">€/mois</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground sm:ml-4">
                      <CalendarClock className="w-4 h-4" />
                      <span>Échéance :</span>
                    </div>
                    <input
                      type="date"
                      data-testid={`renewal-${c.id}`}
                      defaultValue={c.renewal_date ? String(c.renewal_date).slice(0, 10) : ""}
                      onChange={(e) => setRenewal(c.id, e.target.value)}
                      className="h-9 px-3 rounded-lg border border-input bg-card text-sm outline-none focus:ring-2 focus:ring-ring/40"
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
          {contracts.length === 0 && (
            <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground text-sm">
              Aucun contrat. Déposez un PDF ou chargez les contrats démo.
            </div>
          )}
        </div>
      )}

      <CardPicker open={showCards} onClose={() => setShowCards(false)} onAdded={(added) => { load(); onChanged?.(); promptPricesFor(added); }} />
      <CardCompare open={showCompare} onClose={() => setShowCompare(false)} />
      <FormulaChooser
        open={formula.open}
        draft={formula.draft}
        formulas={formula.formulas}
        submitting={formula.submitting}
        existingLabels={existingCoverageLabels}
        onPick={confirmFormula}
        onClose={() => setFormula({ open: false, draft: null, formulas: [], submitting: false })}
      />
      <PriceModal
        open={priceQueue.length > 0}
        contract={priceQueue[0]}
        index={priceTotal - priceQueue.length + 1}
        total={priceTotal}
        onSaved={onPriceDone}
        onClose={() => setPriceQueue((q) => q.slice(1))}
      />
    </div>
  );
}
